import React, { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';

import { Toaster, ToastProvider as BaseToastProvider, useToastManager } from '@playbooks/ui/components/toast';
import { normalizeError } from '@playbooks/utils/errors';

export type ToastContextProps = {
	showSuccess: (title, message) => any;
	showInfo: (title, message) => any;
	showError: (v) => any;
};

export const ToastContext = React.createContext<ToastContextProps>(null);

const ToastBridge = ({ children }) => {
	const manager = useToastManager();
	const router = useRouter();

	// Hooks
	useEffect(() => {
		const onRouteStart = (_url: string, { shallow }: { shallow: boolean }) => {
			if (shallow) return;
			manager.close();
		};

		router.events.on('routeChangeStart', onRouteStart);
		return () => router.events.off('routeChangeStart', onRouteStart);
	}, [manager, router.events]);

	// Methods
	const showSuccess = (title: string, message?: string) => {
		return manager.add({ type: 'success', title, description: message, timeout: 3000 });
	};

	const showInfo = (title: string, message?: string) => {
		return manager.add({ type: 'info', title, description: message, timeout: 3000 });
	};

	const showError = (error: { type?: string; status?: string; title?: string; message?: string }) => {
		const normalized = normalizeError(error);
		return manager.add({
			type: 'error',
			title: normalized.title,
			description: normalized.message,
			timeout: 0,
			priority: 'high',
		});
	};

	const memoizedValues = useMemo(() => {
		return { showSuccess, showInfo, showError };
	}, [manager]);

	// Render
	return (
		<ToastContext.Provider value={memoizedValues}>
			<Toaster />
			{children}
		</ToastContext.Provider>
	);
};

export const ToastProvider = ({ children }) => (
	<BaseToastProvider>
		<ToastBridge>{children}</ToastBridge>
	</BaseToastProvider>
);

export const useToast = () => {
	return React.useContext(ToastContext);
};
